// src/pages/SHGHome.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

const BROADER_TABS = [
  { key: "savings-finance", label: "Savings & Finance", emoji: "💰" },
  { key: "entrepreneurship", label: "Entrepreneurship", emoji: "💼" },
  { key: "training-skill", label: "Training & Skill", emoji: "🎓" },
  { key: "community-work", label: "Community Work", emoji: "👥" },
  { key: "governance", label: "Governance", emoji: "⚖️" },
  { key: "linkages", label: "Linkages", emoji: "🔗" },
  { key: "digital-future", label: "Digital Future", emoji: "🚀" },
];

const SHG_TABS = [
  { key: "register-shg", label: "Register My SHG", emoji: "📝👥" },
  { key: "shg-members", label: "SHG Members", emoji: "👥" },
  { key: "contribute-shg", label: "Contribute to SHG", emoji: "➕💰" },
  { key: "withdrawal-shg", label: "Request Withdrawal", emoji: "💳👥" },
];

const TABS = [...BROADER_TABS, ...SHG_TABS];

const SHGHome = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].key);

  // States for searches in SHG tabs
  const [memberSearch, setMemberSearch] = useState("");
  const [contribSearch, setContribSearch] = useState("");
  const [reqSearch, setReqSearch] = useState("");

  // 📊 Chart data
  const chartData = {
    savingsTrend: {
      labels: ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct"],
      datasets: [
        {
          label: "Monthly Savings (₹)",
          data: [4000, 5200, 5800, 6400, 7000, 7200, 8100],
          borderColor: "#ec4899",
          backgroundColor: "rgba(236, 72, 153, 0.3)",
          tension: 0.4,
        },
      ],
    },
    skillTraining: {
      labels: ["Tailoring", "Digital", "Candle", "Food Processing"],
      datasets: [
        {
          data: [20, 15, 10, 25],
          backgroundColor: [
            "rgba(236, 72, 153, 0.8)",
            "rgba(244, 114, 182, 0.8)",
            "rgba(249, 168, 212, 0.8)",
            "rgba(190, 24, 93, 0.8)",
          ],
          borderWidth: 0,
        },
      ],
    },
    incomeBar: {
      labels: ["Pickle", "Papad", "Tailoring", "Crafts"],
      datasets: [
        {
          label: "Monthly Income (₹)",
          data: [12000, 9000, 7000, 6000],
          backgroundColor: "rgba(236, 72, 153, 0.8)",
          borderRadius: 6,
        },
      ],
    },
  };

  // 🧾 Data by section for broader tabs
  const broaderData = {
    "Savings & Finance": [
      { title: "Monthly Savings", desc: "₹100 collected per member", emoji: "💰" },
      { title: "Internal Loans", desc: "3 loans active @10% interest", emoji: "🏦" },
      { title: "Bank Linkage", desc: "SBI, Thane Branch", emoji: "💳" },
      { title: "Bookkeeping", desc: "Updated till Oct 2025", emoji: "📚" },
    ],
    Entrepreneurship: [
      { title: "Pickle Unit", desc: "250 jars/month", emoji: "🥒" },
      { title: "Tailoring Orders", desc: "Uniforms for 3 schools", emoji: "🧵" },
      { title: "Papad Production", desc: "150 kg/month", emoji: "🍪" },
      { title: "Online Store", desc: "Active on MahilaMandi", emoji: "🛍️" },
    ],
    "Training & Skill": [
      { title: "Digital Literacy", desc: "UPI & WhatsApp business", emoji: "💻" },
      { title: "Candle Making", desc: "10 women trained", emoji: "🕯️" },
      { title: "Entrepreneurship", desc: "Taluka workshop held", emoji: "🎓" },
      { title: "Financial Literacy", desc: "MAVIM session", emoji: "📊" },
    ],
    "Community Work": [
      { title: "Health Camp", desc: "50 women screened", emoji: "🩺" },
      { title: "Clean Village Drive", desc: "Monthly sanitation", emoji: "🧹" },
      { title: "Tree Plantation", desc: "300 saplings", emoji: "🌱" },
      { title: "Girl Education", desc: "2 girls re-enrolled", emoji: "👧📚" },
    ],
    Governance: [
      { title: "Monthly Meeting", desc: "92% attendance", emoji: "📅" },
      { title: "Audit", desc: "FY2024–25 completed", emoji: "🧾" },
      { title: "Leadership", desc: "New president elected", emoji: "👩‍💼" },
      { title: "Records", desc: "Weekly updates done", emoji: "🖊️" },
    ],
    Linkages: [
      { title: "NABARD Fund", desc: "₹50,000 sanctioned", emoji: "🏦" },
      { title: "CSR Support", desc: "Packaging training", emoji: "🤝" },
      { title: "Market Fair", desc: "Sold ₹35,000 worth", emoji: "🏪" },
      { title: "Think5Whys", desc: "Digital Skill Partner", emoji: "🧩" },
    ],
    "Digital Future": [
      { title: "Data Capture", desc: "100% SHG digitized", emoji: "📲" },
      { title: "AI Insights", desc: "Sales forecast ready", emoji: "🤖" },
      { title: "MahilaMandi", desc: "Online marketplace live", emoji: "🛒" },
      { title: "E-Learning", desc: "Skill modules launched", emoji: "📚💡" },
    ],
  };

  // Dummy data for SHG tabs
  const shgDummyData = {
    registerSHG: {
      formFields: [
        { id: "shgName", label: "SHG Name", type: "text", placeholder: "e.g., Mahila Bachat Gaurav SHG" },
        { id: "presidentName", label: "President Name", type: "text", placeholder: "e.g., Sita Devi" },
        { id: "membersCount", label: "Number of Members", type: "number", placeholder: "e.g., 15" },
        { id: "village", label: "Village/Taluka", type: "text", placeholder: "e.g., Thane, Maharashtra" },
        { id: "bankAccount", label: "Bank Account No.", type: "text", placeholder: "e.g., ****1234" },
        { id: "ifsc", label: "IFSC Code", type: "text", placeholder: "e.g., SBIN0001234" },
      ],
      existingSHG: null, // If registered, show details
    },
    shgMembers: {
      members: [
        { id: 1, name: "Sita Devi", role: "President", contact: "+91-9876543210", status: "Active" },
        { id: 2, name: "Geeta Bai", role: "Secretary", contact: "+91-9876543211", status: "Active" },
        { id: 3, name: "Rani Sharma", role: "Member", contact: "+91-9876543212", status: "Active" },
        { id: 4, name: "Lata Patel", role: "Treasurer", contact: "+91-9876543213", status: "Inactive" },
        { id: 5, name: "Meera Singh", role: "Member", contact: "+91-9876543214", status: "Active" },
      ],
    },
    contributeSHG: {
      contributions: [
        { id: 1, date: "2025-11-01", member: "Sita Devi", amount: 100, mode: "Cash" },
        { id: 2, date: "2025-11-02", member: "Geeta Bai", amount: 150, mode: "UPI" },
        { id: 3, date: "2025-11-05", member: "Rani Sharma", amount: 100, mode: "Cash" },
        { id: 4, date: "2025-11-07", member: "Lata Patel", amount: 50, mode: "Bank Transfer" },
      ],
      formFields: [
        { id: "contributorName", label: "Contributor Name", type: "text" },
        { id: "amount", label: "Amount (₹)", type: "number" },
        { id: "mode", label: "Mode", type: "select", options: ["Cash", "UPI", "Bank Transfer"] },
      ],
    },
    withdrawalSHG: {
      requests: [
        { id: 1, date: "2025-11-03", amount: 500, purpose: "Emergency Loan", status: "Approved" },
        { id: 2, date: "2025-11-06", amount: 200, purpose: "Business Investment", status: "Pending" },
        { id: 3, date: "2025-11-08", amount: 300, purpose: "Medical", status: "Rejected" },
      ],
      formFields: [
        { id: "amount", label: "Amount (₹)", type: "number", min: 100 },
        { id: "purpose", label: "Purpose", type: "textarea", placeholder: "e.g., Emergency medical expense" },
        { id: "bankDetails", label: "Bank Details", type: "text", placeholder: "Account No. & IFSC" },
      ],
    },
  };

  const renderTabContent = (tabKey) => {
    const tab = TABS.find(t => t.key === tabKey);
    const label = tab?.label;

    // For broader tabs, show card grid
    if (broaderData[label]) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 justify-center"
        >
          {broaderData[label].map((item, index) => (
            <div
              key={index}
              className="bg-gray-900 border border-pink-700 rounded-2xl p-3 text-center shadow-lg hover:shadow-pink-600/40"
            >
              <div className="text-3xl mb-1">{item.emoji}</div>
              <h3 className="text-base font-bold text-pink-300">{item.title}</h3>
              <p className="text-xs text-gray-400">{item.desc}</p>
            </div>
          ))}
        </motion.div>
      );
    }

    // For SHG tabs
    switch (tabKey) {
      case "register-shg":
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-pink-300">Register Your SHG</h2>
            {shgDummyData.registerSHG.existingSHG ? (
              <div className="bg-green-900/50 p-4 rounded-lg border border-green-500">
                <p className="text-green-300">Your SHG is already registered!</p>
                <p className="text-sm text-gray-400">Name: {shgDummyData.registerSHG.existingSHG.name}</p>
                <button className="mt-2 bg-green-600 text-white px-4 py-2 rounded">Update Details</button>
              </div>
            ) : (
              <form className="space-y-3">
                {shgDummyData.registerSHG.formFields.map((field) => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-pink-300 mb-1">{field.label}</label>
                    {field.type === "select" ? (
                      <select className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100" defaultValue="">
                        <option value="">Select...</option>
                        {field.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : field.type === "textarea" ? (
                      <textarea className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100" rows={3} placeholder={field.placeholder} />
                    ) : (
                      <input type={field.type} className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100" placeholder={field.placeholder} />
                    )}
                  </div>
                ))}
                <button type="submit" className="w-full bg-pink-500 text-white py-2 rounded hover:bg-pink-600">Register SHG</button>
              </form>
            )}
            <div className="text-xs text-gray-400">
              <p>Registration links your SHG to government schemes like NRLM & MAVIM.</p>
            </div>
          </div>
        );
      case "shg-members":
        const filteredMembers = shgDummyData.shgMembers.members.filter((member) =>
          member.name.toLowerCase().includes(memberSearch.toLowerCase())
        );
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-pink-300">SHG Members ({filteredMembers.length})</h2>
            <input
              type="text"
              placeholder="Search members..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100"
            />
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {filteredMembers.map((member) => (
                <div key={member.id} className="bg-gray-800 p-3 rounded border border-pink-700 flex justify-between items-center">
                  <div>
                    <p className="font-medium text-pink-300">{member.name}</p>
                    <p className="text-xs text-gray-400">{member.role} | {member.contact}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${member.status === "Active" ? "bg-green-600" : "bg-red-600"}`}>{member.status}</span>
                </div>
              ))}
            </div>
            <button className="w-full bg-pink-500 text-white py-2 rounded hover:bg-pink-600">Add New Member</button>
          </div>
        );
      case "contribute-shg":
        const filteredContributions = shgDummyData.contributeSHG.contributions.filter((contrib) =>
          contrib.member.toLowerCase().includes(contribSearch.toLowerCase())
        );
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-pink-300">Contributions</h2>
            <input
              type="text"
              placeholder="Search contributions..."
              value={contribSearch}
              onChange={(e) => setContribSearch(e.target.value)}
              className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100"
            />
            <div className="bg-gray-800 p-3 rounded border border-pink-700 space-y-2 max-h-40 overflow-y-auto">
              {filteredContributions.map((contrib) => (
                <div key={contrib.id} className="flex justify-between text-sm">
                  <span>{contrib.member} - ₹{contrib.amount} ({contrib.mode})</span>
                  <span className="text-gray-400">{contrib.date}</span>
                </div>
              ))}
            </div>
            <form className="space-y-3">
              {shgDummyData.contributeSHG.formFields.map((field) => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-pink-300 mb-1">{field.label}</label>
                  {field.type === "select" ? (
                    <select className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100" defaultValue="">
                      <option value="">Select...</option>
                      {field.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input type={field.type} className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100" placeholder={field.placeholder} />
                  )}
                </div>
              ))}
              <button type="submit" className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700">Contribute Now</button>
            </form>
          </div>
        );
      case "withdrawal-shg":
        const filteredRequests = shgDummyData.withdrawalSHG.requests.filter((req) =>
          req.purpose.toLowerCase().includes(reqSearch.toLowerCase())
        );
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-pink-300">Withdrawal Requests</h2>
            <input
              type="text"
              placeholder="Search requests..."
              value={reqSearch}
              onChange={(e) => setReqSearch(e.target.value)}
              className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100"
            />
            <div className="bg-gray-800 p-3 rounded border border-pink-700 space-y-2 max-h-40 overflow-y-auto">
              {filteredRequests.map((req) => (
                <div key={req.id} className="flex justify-between text-sm">
                  <span>{req.purpose} - ₹{req.amount}</span>
                  <span className={`text-xs px-2 py-1 rounded ${req.status === "Approved" ? "bg-green-600" : req.status === "Rejected" ? "bg-red-600" : "bg-yellow-600"}`}>{req.status}</span>
                  <span className="text-gray-400">{req.date}</span>
                </div>
              ))}
            </div>
            <form className="space-y-3">
              {shgDummyData.withdrawalSHG.formFields.map((field) => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-pink-300 mb-1">{field.label}</label>
                  {field.type === "textarea" ? (
                    <textarea className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100" rows={3} placeholder={field.placeholder} />
                  ) : (
                    <input type={field.type} className="w-full bg-gray-800 border border-pink-500 rounded p-2 text-pink-100" placeholder={field.placeholder} min={field.min} />
                  )}
                </div>
              ))}
              <button type="submit" className="w-full bg-orange-600 text-white py-2 rounded hover:bg-orange-700">Request Withdrawal</button>
            </form>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black text-pink-400 p-4 font-sans">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-4"
      >
        <h1 className="text-xl lg:text-2xl font-bold text-pink-500">
          🌸 Mahila Bachat SHG Dashboard
        </h1>
        <p className="text-sm text-gray-400">
          Empowering Women • Strengthening Communities
        </p>
      </motion.div>

      {/* Charts Section */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
        <div className="bg-gray-900 p-3 rounded-xl border border-pink-700">
          <h3 className="text-center text-sm mb-2 text-pink-300">
            💰 Savings Growth
          </h3>
          <Line data={chartData.savingsTrend} options={{ plugins: { legend: { display: false } }, scales: { x: { ticks: { color: "#f9a8d4" } }, y: { ticks: { color: "#f9a8d4" } } } }} />
        </div>

        <div className="bg-gray-900 p-3 rounded-xl border border-pink-700">
          <h3 className="text-center text-sm mb-2 text-pink-300">
            🧵 Skill Training Distribution
          </h3>
          <Doughnut data={chartData.skillTraining} options={{ plugins: { legend: { labels: { color: "#f9a8d4" } } } }} />
        </div>

        <div className="bg-gray-900 p-3 rounded-xl border border-pink-700">
          <h3 className="text-center text-sm mb-2 text-pink-300">
            🛍️ Income by Product
          </h3>
          <Bar data={chartData.incomeBar} options={{ plugins: { legend: { display: false } }, scales: { x: { ticks: { color: "#f9a8d4" } }, y: { ticks: { color: "#f9a8d4" } } } }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 justify-center gap-2 mb-2">
        {TABS.map((tab) => (
          <motion.button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            whileHover={{ scale: 1.05 }}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === tab.key
                ? "bg-pink-500 text-white shadow-lg"
                : "bg-gray-800 text-pink-300 hover:bg-gray-700"
            }`}
          >
            <span className="text-xl">{tab.emoji}</span>
            <span className="text-xs">{tab.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900 rounded-xl p-4 border border-pink-700 min-h-[400px]"
      >
        {renderTabContent(activeTab)}
      </motion.div>

      {/* Footer */}
      <div className="text-center mt-6 text-gray-500 text-xs">
        © 2025 Think5Whys • SAKSHAM Shakti • MahilaMandi
      </div>
    </div>
  );
};

export default SHGHome;